import type { SurveyAttribute } from '@engagement-web-app/common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router';
import { SaveIcon } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/Button';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Form } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { Section, SectionHeader } from '@/components/Section';
import { Select } from '@/components/Select';
import { Skeleton } from '@/components/Skeleton';
import { showErrorToast, showSuccessToast } from '@/components/Toasts';
import { fetchSurveys, fetchSurveysAttributes, saveAttributeSettingTypes } from '@/queries/surveys/system';

export const Route = createFileRoute('/system/_auth/organizations/$organizationId/survey-settings/')({
    component: SystemShowOrganizationSurveySettingsRoute,
    loader: async ({ params }) => {
        const surveys = await fetchSurveys(params.organizationId);
        return { surveys: surveys.surveys };
    },
    pendingComponent: SystemShowOrganizationSurveySettingsRouteSkeleton,
    errorComponent: SystemShowOrganizationSurveySettingsRouteError,
});

function SystemShowOrganizationSurveySettingsRouteError({ reset }: ErrorComponentProps) {
    return <ErrorOverlay active onRetry={reset} />;
}

function SystemShowOrganizationSurveySettingsRoute() {
    const { organizationId } = Route.useParams();
    const { surveys } = Route.useLoaderData();
    const [surveyId, setSurveyId] = useState(surveys[0]?.surveyId ?? '');

    const attributesQuery = useQuery({
        queryKey: ['surveysAttributes', organizationId, [surveyId]],
        queryFn: () => fetchSurveysAttributes(organizationId, [surveyId]),
        enabled: surveyId !== '',
    });

    if (surveys.length === 0) {
        // TODO: Visual design
        return (
            <Section>
                <SectionHeader
                    title="サーベイ選択"
                    description="設定対象のサーベイを選択します。このクライアントにサーベイが取り込まれると、ここで選択できるようになります。"
                />
                {/* TODO: Visual design for empty state */}
                <div>サーベイがありません。</div>
            </Section>
        );
    }

    return (
        <Flex direction="column" gap="5">
            <Section>
                <SectionHeader
                    title="サーベイ選択"
                    description="設定対象のサーベイを選択します。このページの設定は、ここで選んだサーベイにのみ適用されます。"
                />

                <Flex justify="start">
                    <Select value={surveyId} onChange={(e) => setSurveyId(e.target.value)}>
                        {surveys.map((s) => (
                            <option key={s.surveyId} value={s.surveyId}>
                                {s.surveyName}
                            </option>
                        ))}
                    </Select>
                </Flex>
            </Section>

            <Section>
                <SectionHeader
                    title="組織設定"
                    description="サーベイの属性のうち、どれを「組織」として扱うかを指定します。ここで指定した属性が、サーベイ分析の組織別ランキングの集計単位になります。"
                />

                {/* TODO: Add proper error state */}
                {attributesQuery.isError && <div>Error trying to fetch survey attributes</div>}

                {attributesQuery.data ? (
                    // Re-mount when the survey changes so the select re-initialises
                    // from that survey's attributes.
                    <DepartmentSetting
                        key={surveyId}
                        organizationId={organizationId}
                        surveyId={surveyId}
                        attributes={attributesQuery.data.at(0)?.attributes ?? []}
                    />
                ) : (
                    <DepartmentSettingSkeleton />
                )}
            </Section>
        </Flex>
    );
}

function SystemShowOrganizationSurveySettingsRouteSkeleton() {
    return (
        <Flex direction="column" gap="5">
            <Section>
                <Flex direction="column" gap="6">
                    <Flex direction="column" gap="1">
                        <Skeleton.Line width="40%" height="xl" />
                        <Skeleton.Line width="70%" height="sm" />
                    </Flex>

                    <Flex justify="start">
                        <Skeleton.Box width={240} height={42} />
                    </Flex>
                </Flex>
            </Section>

            <Section>
                <Flex direction="column" gap="6">
                    <Skeleton.Line width="30%" height="xl" />

                    <DepartmentSettingSkeleton />
                </Flex>
            </Section>
        </Flex>
    );
}

type DepartmentSettingProps = {
    organizationId: string;
    surveyId: string;
    attributes: SurveyAttribute[];
};

function DepartmentSetting({ organizationId, surveyId, attributes }: DepartmentSettingProps) {
    const queryClient = useQueryClient();

    const savedDepartmentSettingId = attributes.find((attr) => attr.type === 'DEPARTMENT')?.id ?? '';
    const [departmentSettingId, setDepartmentSettingId] = useState(savedDepartmentSettingId);

    const saveMutation = useMutation({
        mutationFn: () =>
            saveAttributeSettingTypes(organizationId, surveyId, {
                types: departmentSettingId ? [{ settingId: departmentSettingId, type: 'DEPARTMENT' }] : [],
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['surveysAttributes', organizationId] });
            showSuccessToast({ title: 'サーベイ設定', description: '組織の設定を更新しました。' });
        },
        onError: () => {
            showErrorToast({ title: 'サーベイ設定', description: '組織の設定の更新に失敗しました。' });
        },
    });

    const isDirty = departmentSettingId !== savedDepartmentSettingId;

    return (
        <Flex direction="column" gap="5">
            <Flex justify="start">
                <Form.Field
                    label="組織の指定"
                    control={(props) => (
                        <Select
                            {...props}
                            value={departmentSettingId}
                            onChange={(e) => setDepartmentSettingId(e.target.value)}
                            fullWidth
                        >
                            <option value="">--組織に当たる属性を選択してください--</option>
                            {attributes.map((attr) => (
                                <option key={attr.id} value={attr.id}>
                                    {attr.name}
                                </option>
                            ))}
                        </Select>
                    )}
                />
            </Flex>

            <Flex justify="start">
                <Button
                    type="button"
                    onClick={() => saveMutation.mutate()}
                    disabled={!isDirty || saveMutation.isPending}
                >
                    <Icon icon={SaveIcon} size="sm" />
                    保存する
                </Button>
            </Flex>
        </Flex>
    );
}

function DepartmentSettingSkeleton() {
    return (
        <Flex direction="column" gap="5">
            <Flex justify="start">
                <Form.FieldSkeleton />
            </Flex>

            <Flex justify="start">
                <Skeleton.Box width={110} height={36} />
            </Flex>
        </Flex>
    );
}
